import { useState } from 'react';
import {
  Text,
  Paper,
  Stack,
  Group,
  Table,
  TextInput,
  ActionIcon,
  Tooltip,
  Badge,
  SegmentedControl,
  Skeleton,
  Pagination,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconFileText,
  IconDownload,
  IconSearch,
} from '@tabler/icons-react';
import PageHeader from '@/components/PageHeader';
import EmptyState from '@/components/EmptyState';
import {
  useCustomerDocuments,
  useDocumentCategories,
  useDownloadDocument,
} from '@/hooks/usePortalApi';
import type { CustomerDoc } from '@/types';
import dayjs from 'dayjs';

const PAGE_SIZE = 20;

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function DocumentCenter() {
  const [categoryFilter, setCategoryFilter] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const { data: categoriesData, isLoading: categoriesLoading } = useDocumentCategories();
  const { data: documentsData, isLoading: documentsLoading } = useCustomerDocuments({
    category: categoryFilter || undefined,
    search: search || undefined,
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
  });

  const downloadDocument = useDownloadDocument();

  const categories = categoriesData ?? [];
  const documents = documentsData?.items ?? [];
  const totalPages = Math.ceil((documentsData?.total ?? 0) / PAGE_SIZE);

  const categoryTabs = [
    { value: '', label: 'All' },
    ...categories.map((cat) => ({
      value: cat.category,
      label: `${cat.label} (${cat.count})`,
    })),
  ];

  const handleCategoryChange = (value: string) => {
    setCategoryFilter(value);
    setPage(1);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.currentTarget.value);
    setPage(1);
  };

  const handleDownload = (doc: CustomerDoc) => {
    downloadDocument.mutate(doc.id, {
      onSuccess: (blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = doc.filename;
        a.click();
        URL.revokeObjectURL(url);
      },
      onError: () => {
        notifications.show({
          title: 'Download failed',
          message: 'Unable to download the document. Please try again.',
          color: 'red',
        });
      },
    });
  };

  const renderDocumentsTable = () => {
    if (documents.length === 0) {
      return (
        <EmptyState
          icon={<IconFileText size={48} />}
          title="No documents found"
          description={
            categoryFilter || search
              ? 'No documents match your search or filter criteria.'
              : 'No documents are available yet.'
          }
        />
      );
    }

    return (
      <Paper withBorder>
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Title</Table.Th>
              <Table.Th>Category</Table.Th>
              <Table.Th>Filename</Table.Th>
              <Table.Th>Version</Table.Th>
              <Table.Th ta="right">Size</Table.Th>
              <Table.Th>Expiry Date</Table.Th>
              <Table.Th w={80} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {documents.map((doc) => (
              <Table.Tr key={doc.id}>
                <Table.Td>
                  <Text size="sm" fw={500} lineClamp={1}>
                    {doc.title}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Badge variant="light" size="sm">
                    {doc.category_label}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" c="dimmed" lineClamp={1}>
                    {doc.filename}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" c="dimmed">
                    {doc.version ?? '--'}
                  </Text>
                </Table.Td>
                <Table.Td ta="right">
                  <Text size="sm" c="dimmed">
                    {formatFileSize(doc.file_size)}
                  </Text>
                </Table.Td>
                <Table.Td>
                  {doc.expiry_date ? (
                    <Text
                      size="sm"
                      c={
                        dayjs(doc.expiry_date).isBefore(dayjs())
                          ? 'red'
                          : 'dimmed'
                      }
                    >
                      {dayjs(doc.expiry_date).format('MMM D, YYYY')}
                    </Text>
                  ) : (
                    <Text size="sm" c="dimmed">
                      --
                    </Text>
                  )}
                </Table.Td>
                <Table.Td>
                  <Tooltip label="Download">
                    <ActionIcon
                      variant="subtle"
                      size="sm"
                      onClick={() => handleDownload(doc)}
                      loading={downloadDocument.isPending}
                    >
                      <IconDownload size={16} />
                    </ActionIcon>
                  </Tooltip>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Paper>
    );
  };

  return (
    <Stack gap="lg">
      <PageHeader
        title="Document Center"
        description="Access your documents, certificates, and specifications"
      />

      {categoriesLoading ? (
        <Skeleton height={36} radius="md" />
      ) : (
        <SegmentedControl
          value={categoryFilter}
          onChange={handleCategoryChange}
          data={categoryTabs}
        />
      )}

      <Group>
        <TextInput
          placeholder="Search documents..."
          leftSection={<IconSearch size={16} />}
          value={search}
          onChange={handleSearchChange}
          w={300}
          size="sm"
        />
      </Group>

      {documentsLoading ? (
        <Stack gap="xs">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} height={50} radius="md" />
          ))}
        </Stack>
      ) : (
        renderDocumentsTable()
      )}

      {totalPages > 1 && (
        <Group justify="center">
          <Pagination total={totalPages} value={page} onChange={setPage} />
        </Group>
      )}
    </Stack>
  );
}
